/**
 * Style mixins for the wedding website
 * This file contains reusable style objects that can be composed together
 */

import { SxProps, Theme } from '@mui/material';
import { colors, borderRadius, typography, shadows } from './constants';

// Button mixins
export const buttonMixins = {
  // Primary action button (brown/tan)
  primaryAction: {
    px: 3,
    py: 1.5,
    borderRadius: borderRadius.button,
    fontFamily: typography.fontFamilies.secondary,
    backgroundColor: colors.secondary.darkSolid,
    color: '#fff',
    '&:hover': {
      backgroundColor: colors.secondary.darkHover,
    },
    textDecoration: 'none',
  } as SxProps<Theme>,

  // Secondary action button (outlined)
  secondaryAction: {
    px: 3,
    py: 1.5,
    borderRadius: borderRadius.button,
    fontFamily: typography.fontFamilies.secondary,
  } as SxProps<Theme>,

  // Navigation button
  navButton: (isActive: boolean) => ({
    fontFamily: typography.fontFamilies.secondary,
    color: colors.primary.main,
    bgcolor: isActive ? colors.secondary.main : 'transparent',
    borderRadius: borderRadius.button,
    px: 2,
    '&:hover': {
      bgcolor: isActive 
        ? colors.secondary.main 
        : 'rgba(45, 48, 71, 0.04)',
    },
  } as SxProps<Theme>),

  // Wide button (used in forms)
  wideButton: {
    width: '90%',
    px: 3,
    py: 1.5,
    borderRadius: borderRadius.button,
    fontFamily: typography.fontFamilies.secondary,
    backgroundColor: colors.secondary.darkSolid,
    color: '#fff',
    '&:hover': {
      backgroundColor: colors.secondary.darkHover,
    },
  } as SxProps<Theme>,
};

// Typography mixins
export const typographyMixins = {
  // Page title
  pageTitle: {
    mb: 2,
    fontFamily: typography.fontFamilies.primary,
    fontWeight: typography.fontWeights.bold,
    textAlign: 'center',
  } as SxProps<Theme>,

  // Section title
  sectionTitle: {
    mb: 2,
    fontFamily: typography.fontFamilies.primary,
    fontWeight: typography.fontWeights.bold,
  } as SxProps<Theme>,

  // Subtitle
  subtitle: {
    mb: 2,
    fontFamily: typography.fontFamilies.primary,
    fontWeight: typography.fontWeights.semiBold,
  } as SxProps<Theme>,

  // Body text
  bodyText: {
    mb: 2,
    fontFamily: typography.fontFamilies.primary,
    textAlign: 'left',
  } as SxProps<Theme>,

  // Centered body text
  centeredBodyText: {
    mb: 2,
    textAlign: 'center',
    maxWidth: '800px',
    mx: 'auto',
  } as SxProps<Theme>,
};

// Card mixins
export const cardMixins = {
  // Basic card
  basicCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: borderRadius.card,
    boxShadow: shadows.card,
    bgcolor: colors.background.card,
  } as SxProps<Theme>,

  // Card with bottom button
  cardWithButton: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: borderRadius.card,
    boxShadow: shadows.card,
    bgcolor: colors.background.card,
    position: 'relative',
    pb: 10, // Add padding at bottom for the button
  } as SxProps<Theme>,

  // Card content
  cardContent: {
    flexGrow: 1,
    p: 3,
  } as SxProps<Theme>,

  // Card media
  cardMedia: {
    objectFit: 'cover',
  } as SxProps<Theme>,

  // Card button container
  cardButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
  } as SxProps<Theme>,
};

// Layout mixins
export const layoutMixins = {
  // Hero section
  heroSection: {
    height: { xs: '100vh', sm: '90vh' },
    width: '100vw',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    position: 'relative',
  } as SxProps<Theme>,

  // Hero content box
  heroContent: {
    textAlign: 'center',
    backgroundColor: colors.background.overlay,
    borderRadius: borderRadius.medium,
    p: { xs: 3, sm: 4 },
    mx: { xs: 3, sm: 4 },
    backdropFilter: 'blur(2px)',
    maxWidth: { xs: '90vw', sm: '600px' },
  } as SxProps<Theme>,

  // Page container
  pageContainer: {
    py: 4,
    maxWidth: '800px !important',
  } as SxProps<Theme>,

  // Section container
  sectionContainer: {
    mb: 4,
  } as SxProps<Theme>,

  // Centered flex container
  centeredFlex: {
    display: 'flex',
    justifyContent: 'center',
    mb: 3,
  } as SxProps<Theme>,

  // Responsive image
  responsiveImage: {
    width: '100%',
    height: 'auto',
    borderRadius: borderRadius.medium,
  } as SxProps<Theme>,

  // Section header with background
  sectionHeader: {
    bgcolor: 'rgba(225, 213, 203, 0.3)',
    p: 1,
    px: 2,
    borderRadius: 20,
    mb: 2,
    width: '100%',
  } as SxProps<Theme>,
};

// Form mixins
export const formMixins = {
  // Form container
  formContainer: {
    textAlign: 'center',
    width: '100%',
  } as SxProps<Theme>,

  // Form section
  formSection: {
    mt: 4,
    mb: 6,
  } as SxProps<Theme>,

  // Form field
  formField: {
    mb: 2,
    backgroundColor: 'white',
  } as SxProps<Theme>,

  // Form actions container
  formActions: {
    display: 'flex',
    justifyContent: 'space-between',
    mt: 4,
  } as SxProps<Theme>,
};
